import type { UserEventAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import type { UserEventType } from '@/client/services/progression/types'

import {
  type GroupBy,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import UserQ from './UserQ'

const UserEventQ = {
  ...makeQueryModelV2<UserEventAttributes>('user_event')([
    'id',
    'user_id',
    'session_id',
    'event_type',
    'event_payload',
    'event_points',
    'processed_with',
    'created_at',
    'deleted_at',
  ]),

  where: (
    {
      userId,
      eventType,
      paranoid = true,
      groupBy = null,
      order = null,
    }: {
      userId?: number | null | SQLFragment<number>
      eventType?: UserEventType['type']
      paranoid?: boolean
      groupBy?: GroupBy | null
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<UserEventAttributes> => SQL`
        FROM ${UserEventQ}
        WHERE
            ${userId != null ? SQL`${UserEventQ.user_id} = ${userId} AND` : SQL``}
            ${eventType != null ? SQL`${UserEventQ.event_type} = ${eventType} AND` : SQL``}
            ${paranoid ? SQL`${UserEventQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  getPastHostedSessionIds: ({
    userId,
    requireLearnerAttendance,
  }: {
    userId: number | SQLFragment<number>
    requireLearnerAttendance: boolean
  }) =>
    SQL`(
      SELECT DISTINCT(host_user_event.session_id)::int
      FROM ${UserEventQ} host_user_event
      WHERE
        host_user_event.event_type = 'session_hosted'
        AND host_user_event.user_id = ${userId}
        ${
          requireLearnerAttendance
            ? SQL`
          AND EXISTS (
            SELECT 1
            FROM ${UserEventQ} attendee_user_event
            WHERE
              attendee_user_event.event_type = 'session_attended'
              AND (attendee_user_event.session_id)::int = (host_user_event.session_id)::int
          )
        `
            : SQL``
        }
    )`,

  countPastHostedSessions: ({
    userId,
    requireLearnerAttendance,
  }: {
    userId: number | SQLFragment<number>
    requireLearnerAttendance: boolean
  }): SQLFragment<number> =>
    SQL`(SELECT COUNT(*) FROM ${UserEventQ.getPastHostedSessionIds({ userId, requireLearnerAttendance })})`,

  countLearnersImpacted: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<number> =>
    SQL`(
      SELECT COUNT(DISTINCT ${UserEventQ.user_id})
      FROM ${UserEventQ}
      WHERE
        ${UserEventQ.event_type} = 'session_attended'
        AND (${UserEventQ.session_id})::int = ANY(${UserEventQ.getPastHostedSessionIds({ userId, requireLearnerAttendance: true })})
    )`,

  countCountriesImpacted: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<number> =>
    SQL`(
      SELECT COUNT(DISTINCT learner.country)
      FROM ${UserEventQ}
      JOIN ${UserQ} learner ON learner.id = ${UserEventQ.user_id}
      WHERE
        ${UserEventQ.event_type} = 'session_attended'
        AND (${UserEventQ.session_id})::int = ANY(${UserEventQ.getPastHostedSessionIds({ userId, requireLearnerAttendance: true })})
    )`,
}

export default UserEventQ
